import { useEffect, useMemo, useState } from "react";
import { buildCashTimeline } from "./cashTimeline";

const PLAY_TICK_MS = 80;
const PLAY_TICKS_END_TO_END = 150; // ~12 s to play the whole timeline

/**
 * View state for the cash-flow timeline. None of it is saved: the payment
 * mode and the scope toggles are "what if" views over the saved schedule.
 *
 * The cursor follows "today" until the user moves it, so the page opens on
 * the current position.
 */
export default function useCashTimeline(schedule) {
  const [mode, setMode] = useState("sequenced");
  const [excluded, setExcluded] = useState(() => new Set());
  const [selectedCode, setSelectedCode] = useState(null);
  const [pickedDay, setPickedDay] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const timeline = useMemo(
    () => buildCashTimeline(schedule, { mode, excluded }),
    [schedule, mode, excluded],
  );

  const cursorDay = Math.min(timeline.days, Math.max(0, pickedDay ?? timeline.todayDay));

  useEffect(() => {
    if (!isPlaying) return undefined;
    const step = Math.max(1, Math.round(timeline.days / PLAY_TICKS_END_TO_END));
    const timer = setInterval(() => {
      setPickedDay((day) => Math.min(timeline.days, (day ?? 0) + step));
    }, PLAY_TICK_MS);
    return () => clearInterval(timer);
  }, [isPlaying, timeline.days]);

  useEffect(() => {
    if (isPlaying && cursorDay >= timeline.days) setIsPlaying(false);
  }, [isPlaying, cursorDay, timeline.days]);

  const moveCursor = (day) => {
    setIsPlaying(false);
    setPickedDay(day);
  };

  const togglePlay = () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    if (cursorDay >= timeline.days) setPickedDay(0);
    else setPickedDay(cursorDay);
    setIsPlaying(true);
  };

  const toggleLineInScope = (code) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const toggleSelectedLine = (code) =>
    setSelectedCode((prev) => (prev === code ? null : code));

  return {
    timeline,
    mode,
    setMode,
    excluded,
    toggleLineInScope,
    selectedCode,
    toggleSelectedLine,
    cursorDay,
    moveCursor,
    isPlaying,
    togglePlay,
  };
}
